import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	filterAndSortByQuery,
	formatPercentage,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components/Icon";
import { SearchField } from "../../../../Components/Search";
import {
	CourseSection,
	Student,
	StudentCourseSectionGradeBookQuery,
	StudentNameAndImageFragment,
	useSearchFormState,
	useStudentCourseSectionGradeBookQuery,
} from "../../../../Utilities";
import { CourseAssessmentFormat, CourseAssessmentSubmissionFormat, StudentFormat } from "../../../Format";
import { MediaAttachmentsSummary } from "../../MediaAttachment";
import { StudentIcon } from "../../Student";
import { SubmissionStatusBadge } from "../SubmissionStatusBadge";
import styles from "./styles.module.css";

export type StudentCourseSectionGradeBookModalProps = Omit<
	StudentCourseSectionGradeBookProps,
	"studentID" | "className" | "style"
> & {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	withBackButton?: boolean;
	backButtonText?: string;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function StudentCourseSectionGradeBookModal({
	student,
	courseSectionID,
	withBackButton = false,
	backButtonText = "Back",
	ifRef,
	onClose,
	className,
	style,
}: StudentCourseSectionGradeBookModalProps) {
	return (
		<Modal size="full" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ marginRight: "1.5rem" }}>
					<If condition={withBackButton}>
						{onClose && (
							<Button variant="link" size="small" onClick={onClose}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
									<Icon.ArrowLeft size={14} /> {backButtonText}
								</Row>
							</Button>
						)}
					</If>

					<Row justify="spaced-start" align="center">
						<StudentIcon student={student} size={60} />
						<Heading.H2 noMargin className={styles["student-name"]}>
							{StudentFormat.name(student)}
						</Heading.H2>
					</Row>
				</Column>
			</Modal.Header>
			<Modal.Body>
				<StudentCourseSectionGradeBook studentID={student.id} courseSectionID={courseSectionID} />
			</Modal.Body>
		</Modal>
	);
}

const columns = ["assessment", "status", "submissionDate", "points", "attachments", "notes"];

export type StudentCourseSectionGradeBookProps = {
	studentID: Student["id"];
	courseSectionID: CourseSection["id"];
} & Partial<ClassNameProps & StyleProps>;

export function StudentCourseSectionGradeBook({
	studentID,
	courseSectionID,
	className,
	style,
}: StudentCourseSectionGradeBookProps) {
	const searchFormState = useSearchFormState();
	const { search } = searchFormState.formValues;

	const { loading, data, error } = useStudentCourseSectionGradeBookQuery({
		variables: { studentID, courseSectionID, filters: { dueOnLTE: toLocalDateString(new Date()) } },
	});
	// TODO pagination
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "courseAssessmentTimingConnection" });

	const summary = React.useMemo(() => {
		let pointsEarned = 0;
		let totalPoints = 0;
		let turnedIn = 0;
		let late = 0;
		let notTurnedIn = 0;

		if (data) {
			for (let i = 0; i < data.courseAssessmentTimingConnection.edges.length; i++) {
				const courseAssessment = data.courseAssessmentTimingConnection.edges[i].node.courseAssessment;
				totalPoints += courseAssessment.totalPoints;
				const submission = courseAssessment.courseAssessmentSubmissions[0];
				const mark = courseAssessment.courseAssessmentMarks[0];
				if (mark) {
					if (mark.isExcused) {
						continue;
					}
					pointsEarned += mark.points;
				}
				if (mark && mark.wasLate) {
					late++;
				} else if (submission) {
					turnedIn++;
				} else {
					notTurnedIn++;
				}
			}
		}

		return {
			overallGrade: pointsEarned / totalPoints,
			turnedIn,
			late,
			notTurnedIn,
		};
	}, [data]);

	const filteredTimingEdges = React.useMemo(() => {
		if (!data) {
			return [];
		}
		return filterAndSortByQuery(search, data.courseAssessmentTimingConnection.edges, (e) =>
			CourseAssessmentFormat.name(e.node.courseAssessment),
		);
	}, [search, data]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<Row justify="spaced-start" horizontalSpacing="3.75rem" className={styles["summary"]}>
				<Column justify="spaced-start">
					<span className={styles["summary__item__label"]}>Overall Grade</span>
					<span className={styles["summary__item__value"]}>{formatPercentage(summary.overallGrade)}</span>
				</Column>
				<Column justify="spaced-start">
					<span className={styles["summary__item__label"]}>Turned In</span>
					<Row
						justify="spaced-start"
						align="center"
						horizontalSpacing="0.25rem"
						className={styles["summary__item__value"]}
					>
						<Icon.CheckCircle
							color={summary.turnedIn > 0 ? "var(--theme-colors__green)" : "var(--neutral-colors--quaternary)"}
						/>
						{summary.turnedIn}
					</Row>
				</Column>
				<Column justify="spaced-start">
					<span className={styles["summary__item__label"]}>Late</span>
					<Row
						justify="spaced-start"
						align="center"
						horizontalSpacing="0.25rem"
						className={styles["summary__item__value"]}
					>
						<Icon.WarningCircle
							color={summary.late > 0 ? "var(--theme-colors__yellow)" : "var(--neutral-colors--quaternary)"}
						/>
						{summary.late}
					</Row>
				</Column>
				<Column justify="spaced-start">
					<span className={styles["summary__item__label"]}>Not Turned In</span>
					<Row
						justify="spaced-start"
						align="center"
						horizontalSpacing="0.25rem"
						className={styles["summary__item__value"]}
					>
						<Icon.WarningOctagon
							color={summary.notTurnedIn > 0 ? "var(--theme-colors__pink)" : "var(--neutral-colors--quaternary)"}
						/>
						{summary.notTurnedIn}
					</Row>
				</Column>
			</Row>

			<SearchField formState={searchFormState} name="search" />

			<Table columns={columns} className={styles["table"]}>
				<Table.Header>
					<Table.Row>
						<Table.Heading column="assessment" className={styles["table__heading"]}>
							Assessment
						</Table.Heading>
						<Table.Heading column="status" className={styles["table__heading"]}>
							Status
						</Table.Heading>
						<Table.Heading column="submissionDate" className={styles["table__heading"]}>
							Turn In Date
						</Table.Heading>
						<Table.Heading column="points" className={styles["table__heading"]}>
							Points
						</Table.Heading>
						<Table.Heading column="attachments" className={styles["table__heading"]}>
							Attachments
						</Table.Heading>
						<Table.Heading column="notes" className={styles["table__heading"]}>
							Comments
						</Table.Heading>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					<Conditional>
						<If condition={queryStatus === "results"}>
							{filteredTimingEdges.map(({ node: timing }) => {
								const submission = timing.courseAssessment.courseAssessmentSubmissions[0] as
									| StudentCourseSectionGradeBookQuery["courseAssessmentTimingConnection"]["edges"][0]["node"]["courseAssessment"]["courseAssessmentSubmissions"][0]
									| undefined;
								const mark = timing.courseAssessment.courseAssessmentMarks[0] as
									| StudentCourseSectionGradeBookQuery["courseAssessmentTimingConnection"]["edges"][0]["node"]["courseAssessment"]["courseAssessmentMarks"][0]
									| undefined;

								return (
									<Table.Row key={timing.id}>
										<Table.Cell column="assessment" oneLine className={styles["table__cell"]}>
											<span className={styles["assessment-name"]}>
												{CourseAssessmentFormat.name(timing.courseAssessment)}
											</span>
										</Table.Cell>
										<Table.Cell
											column="status"
											className={styles["table__cell"]}
											style={{ paddingTop: 0, paddingBottom: 0 }}
										>
											<Column justify="center" style={{ height: "100%" }}>
												<SubmissionStatusBadge courseAssessmentSubmission={submission} courseAssessmentMark={mark} />
											</Column>
										</Table.Cell>
										<Table.Cell column="submissionDate" className={styles["table__cell"]}>
											{submission
												? CourseAssessmentSubmissionFormat.Fields.submissionDate(submission.submissionDate)
												: "-"}
										</Table.Cell>
										<Table.Cell column="points" className={styles["table__cell"]}>
											{mark && mark.isExcused
												? "-"
												: `${mark ? mark.points : "-"}/${timing.courseAssessment.totalPoints}`}
										</Table.Cell>
										<Table.Cell column="attachments" className={styles["table__cell"]}>
											{submission && submission.mediaAttachments.length > 0 ? (
												<MediaAttachmentsSummary mediaAttachments={submission.mediaAttachments} />
											) : (
												"-"
											)}
										</Table.Cell>
										<Table.Cell column="notes" className={styles["table__cell"]}>
											{mark && mark.notes ? mark.notes : "-"}
										</Table.Cell>
									</Table.Row>
								);
							})}
						</If>
						<Else>
							<Table.Row>
								<Table.Cell column={columns[0]} colspan={columns.length}>
									<Conditional>
										<If condition={queryStatus === "no-results"}>No assessments</If>
										<If condition={queryStatus === "error"}>Something went wrong</If>
										<If condition={queryStatus === "loading"}>Loading...</If>
									</Conditional>
								</Table.Cell>
							</Table.Row>
						</Else>
					</Conditional>
				</Table.Body>
			</Table>
		</Column>
	);
}
